/**
 * Module-level cache for the check-in T&C template catalog.
 *
 * Same shape as ``useQuoteTermsTemplates`` / ``useAppSettings``: one shared
 * fetch across the app, invalidation notifies live consumers, synchronous
 * accessors for non-React readers. Templates change rarely (admin-driven)
 * and the payload is tiny — a shared module cache is the right size.
 *
 * The catalog holds templates for **all** intake types; callers narrow by
 * ``intakeType`` themselves (the admin groups by it; a default lookup is
 * scoped by it).
 */

import { useEffect, useState } from 'react';
import type {
  CheckInTermsIntakeType,
  CheckInTermsTemplate,
} from '@techgitter/contracts';

import { fetchCheckInTermsTemplates } from '../services/checkInTermsTemplatesApi';

let cache: CheckInTermsTemplate[] | null = null;
let inflight: Promise<CheckInTermsTemplate[]> | null = null;
const subscribers = new Set<() => void>();

const getCatalog = (): Promise<CheckInTermsTemplate[]> => {
  if (cache !== null) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchCheckInTermsTemplates()
      .then((items) => {
        cache = items;
        return items;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

/** Discard the cached catalog and notify live consumers. Call after any
 *  successful create / update / delete / set-default. */
export function invalidateCheckInTermsTemplatesCache(): void {
  cache = null;
  inflight = null;
  subscribers.forEach((notify) => {
    try {
      notify();
    } catch {
      /* keep other subscribers running on a single failure */
    }
  });
}

/** Synchronous read. Returns ``null`` until the first fetch resolves. */
export function getCachedCheckInTermsTemplates(): CheckInTermsTemplate[] | null {
  return cache;
}

/** Convenience: the currently-default template for an intake type (or null
 *  when the catalog hasn't loaded / has no default for that type). */
export function getDefaultCheckInTermsTemplate(
  intakeType: CheckInTermsIntakeType,
): CheckInTermsTemplate | null {
  if (!cache) return null;
  return (
    cache.find((t) => t.intakeType === intakeType && t.isDefault) ?? null
  );
}

export interface UseCheckInTermsTemplatesResult {
  templates: CheckInTermsTemplate[];
  isLoading: boolean;
  error: string | null;
}

export function useCheckInTermsTemplates(): UseCheckInTermsTemplatesResult {
  const [templates, setTemplates] = useState<CheckInTermsTemplate[]>(cache ?? []);
  const [isLoading, setIsLoading] = useState(cache === null);
  const [error, setError] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    const onInvalidate = () => setRev((r) => r + 1);
    subscribers.add(onInvalidate);
    return () => {
      subscribers.delete(onInvalidate);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(cache === null);
    getCatalog()
      .then((items) => {
        if (cancelled) return;
        setTemplates(items);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rev]);

  return { templates, isLoading, error };
}
